import { Component, computed, effect, inject, signal } from '@angular/core';
import {
  CapitalizePipe,
  EditorInsertLinkDirective,
  errorToast,
  FileUploadComponent,
  FormErrorService,
  Media,
  MediaService,
  RcMultiSelectWrapPaddingDirective,
  RcSelectableOverflowDirective,
  RoyalCrownSenderEmail,
  RoyalCrownSenderEmailService,
  successSentToast,
  SupplierComplaint,
  SupplierComplaintSendInput,
  SupplierComplaintService,
  ValidationDirective,
  DisabledDirective,
  DropdownLoaderDirective,
} from 'shared';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { DialogActionsComponent, DialogContentBase, DialogRef } from '@progress/kendo-angular-dialog';
import { NotificationService } from '@progress/kendo-angular-notification';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, take, tap } from 'rxjs';
import {
  DropDownFilterSettings,
  DropDownListComponent,
  MultiSelectComponent,
  PreventableEvent,
} from '@progress/kendo-angular-dropdowns';
import {
  EditorBoldButtonDirective,
  EditorComponent,
  EditorCssSettings,
  EditorItalicButtonDirective,
  EditorStrikethroughButtonDirective,
  EditorUnderlineButtonDirective,
  EditorUnlinkButtonDirective,
} from '@progress/kendo-angular-editor';
import { ToolBarButtonComponent, ToolBarButtonGroupComponent, ToolBarComponent } from '@progress/kendo-angular-toolbar';
import { ButtonComponent } from '@progress/kendo-angular-buttons';
import { SkeletonComponent } from '@progress/kendo-angular-indicators';
import { FormFieldComponent, TextBoxComponent } from '@progress/kendo-angular-inputs';
import { LabelComponent } from '@progress/kendo-angular-label';
import { AsyncPipe, LowerCasePipe, NgClass } from '@angular/common';
import { FileRestrictions } from '@progress/kendo-angular-upload';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { environment } from '@environments';

@Component({
  selector: 'rc-send-supplier-complaint-modal',
  imports: [
    DisabledDirective,
    ReactiveFormsModule,
    TranslateModule,
    CapitalizePipe,
    ValidationDirective,
    AsyncPipe,
    LowerCasePipe,
    NgClass,
    FormFieldComponent,
    TextBoxComponent,
    LabelComponent,
    DropDownListComponent,
    MultiSelectComponent,
    RcMultiSelectWrapPaddingDirective,
    EditorComponent,
    EditorBoldButtonDirective,
    EditorItalicButtonDirective,
    EditorUnderlineButtonDirective,
    EditorStrikethroughButtonDirective,
    EditorUnlinkButtonDirective,
    EditorInsertLinkDirective,
    ToolBarComponent,
    ToolBarButtonComponent,
    ToolBarButtonGroupComponent,
    DialogActionsComponent,
    ButtonComponent,
    FileUploadComponent,
    RcSelectableOverflowDirective,
    SkeletonComponent,
    DropdownLoaderDirective,
  ],
  providers: [FormErrorService, TranslatePipe, CapitalizePipe],
  templateUrl: './send-supplier-complaint-modal.component.html',
  styleUrl: './send-supplier-complaint-modal.component.scss',
})
export class SendSupplierComplaintModalComponent extends DialogContentBase {
  #royalCrownSenderEmailService = inject(RoyalCrownSenderEmailService);
  #notificationService = inject(NotificationService);
  #supplierComplaintService = inject(SupplierComplaintService);
  #mediaService = inject(MediaService);

  supplierComplaint = signal<SupplierComplaint | undefined>(undefined);
  loading = signal<boolean>(true);
  isUploading = signal<boolean>(false);
  media = signal<Media[]>([]);
  hasDocuments = computed(() => this.media().length > 0 || this.isUploading());

  supplierComplaintSendForm = signal<FormGroup<SupplierComplaintSendForm>>(this.#initForm());

  royalCrownSenderEmails$: Observable<RoyalCrownSenderEmail[]> = this.#royalCrownSenderEmailService.get();

  uploadUrl = '/api/supplier_complaint_medias';

  protected readonly environment = environment;
  cssSettings: EditorCssSettings = {
    content:
      ".k-content {font-family: 'Work Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important; height: 100%;}",
  };
  filterSettings: DropDownFilterSettings = {
    caseSensitive: false,
    operator: 'contains',
  };
  fileRestrictions: FileRestrictions = {
    allowedExtensions: [
      '.jpg',
      '.jpeg',
      '.png',
      '.gif',
      '.pdf',
      '.xlxs',
      '.xml',
      '.docx',
      '.doc',
      '.csv',
      '.xls',
      '.xlsx',
    ],
  };
  extraData: { key: string; value: string } | null = null;

  constructor() {
    super(inject(DialogRef));

    effect(() => {
      const sc = this.supplierComplaint();
      if (!sc) return;
      this.extraData = { key: 'supplierComplaint', value: sc['@id']! };
      this.#patchWithPrefill(sc);
      this.#mediaService.getAllMedias(`${sc['@id']!}/medias`).subscribe((medias) => {
        this.media.set(medias);
        this.supplierComplaintSendForm().patchValue({ attachments: medias.map((m) => m['@id'] as string) });
      });
    });
  }

  #initForm() {
    const form = new FormGroup<SupplierComplaintSendForm>({
      to: new FormControl([], {
        nonNullable: true,
        validators: [Validators.required],
      }),
      from: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      subject: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      body: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      attachments: new FormControl([], {
        nonNullable: true,
      }),
    });
    form.updateValueAndValidity();
    return form;
  }

  #patchWithPrefill(complaint: SupplierComplaint) {
    this.#supplierComplaintService
      .getPrefill(complaint['@id']!)
      .pipe(
        tap((result) => {
          // the attachments are the media of the complaint, whichever of the two answers first
          this.supplierComplaintSendForm().patchValue({
            ...result,
            attachments: this.media().map((m) => m['@id'] as string),
          });
          this.loading.set(false);
        }),
      )
      .subscribe();
  }

  onSubmit() {
    if (this.supplierComplaintSendForm().invalid) return;

    const sc = this.supplierComplaint()!;
    const value = this.supplierComplaintSendForm().value as SupplierComplaintSendInput;

    this.#supplierComplaintService
      .send(sc['@id']!, value)
      .pipe(take(1))
      .subscribe({
        next: () => {
          const n = this.#notificationService.show(successSentToast);
          if (n.content) n.content.instance.value = 'SUPPLIER_COMPLAINT';
          this.dialog.close({ isSuccessFull: true });
        },
        error: (err: HttpErrorResponse) => {
          const n = this.#notificationService.show(errorToast);
          if (n.content) n.content.instance.setCurrentValue(err);
        },
      });
  }

  getData(entries: RoyalCrownSenderEmail[] | null) {
    return entries ?? [];
  }

  /**
   * There are no addresses to pick from, so the popup stays closed until an address is typed. Kendo only turns the typed
   * text into a recipient while the popup is open: blocked then, Enter submits the form and the text is lost.
   */
  onOpen(event: PreventableEvent, recipients: MultiSelectComponent) {
    if (!recipients.text) event.preventDefault();
  }

  updateDocumentMediaForm(event: { status: boolean; attachments: Media[] }) {
    const newlyAdded = event.attachments.filter((a) => !this.media().some((m) => m['@id'] === a['@id']));
    this.media.set([...this.media(), ...newlyAdded]);
    this.supplierComplaintSendForm().patchValue({ attachments: this.media().map((a) => a['@id'] as string) });
  }

  removeMedia(mediaIri: string) {
    this.#mediaService.deleteMedia(mediaIri).subscribe({
      next: () => {
        this.media.set(this.media().filter((m) => m['@id'] !== mediaIri));
        this.supplierComplaintSendForm().patchValue({ attachments: this.media().map((a) => a['@id'] as string) });
      },
      error: (err: HttpErrorResponse) => {
        const n = this.#notificationService.show(errorToast);
        if (n.content) n.content.instance.setCurrentValue(err);
      },
    });
  }

  close() {
    this.supplierComplaintSendForm().reset();
    this.dialog.close();
  }
}

interface SupplierComplaintSendForm {
  to: FormControl<string[]>;
  from: FormControl<string>;
  subject: FormControl<string>;
  body: FormControl<string>;
  attachments: FormControl<string[]>;
}
